import Cookies from 'js-cookie';
import { setLastActiveCookie } from './cookie';
import { AUTH_COOKIES } from '../constants/cookies';
import { getAccessToken, removeAuthTokens, removeCartCookie, removeOrderGrooveToken } from './cookies';
import { apiFetch } from './apiFetch';

const INACTIVITY_THRESHOLD = 30 * 60 * 1000;
const REFRESH_TIME = 5 * 60 * 1000;

export const isUserLoggedIn = () => {
    const accessToken = Cookies.get(AUTH_COOKIES.ACCESS_TOKEN);
    return !!accessToken;
};

export const checkLastActive = () => {
    const lastActiveCookie = Cookies.get(AUTH_COOKIES.LAST_ACTIVE);

    if (!lastActiveCookie) {
        return false;
    }

    const lastActiveTimestamp = parseInt(lastActiveCookie, 10);
    const currentTime = Date.now();

    return currentTime - lastActiveTimestamp >= INACTIVITY_THRESHOLD;
};

export const isTokenExpiringSoon = () => {
    const tokenExpiry = Cookies.get(AUTH_COOKIES.TOKEN_EXPIRY);

    if (!tokenExpiry) {
        return false;
    }

    const tokenExpiryTime = parseInt(tokenExpiry, 10);
    const currentTime = Date.now();

    return tokenExpiryTime - currentTime <= REFRESH_TIME;
};

type AuthResponse = {
    success: boolean;
    data: {
        code: number;
    };
};

export async function logoutUser(
    /** optionally passed in from FE */
    redirectUrl?: string,
    /** optionally passed in from useCart */
    clearCart?: () => void
) {
    try {
        const accessToken = await getAccessToken();
        const accessTokenSoft = Cookies.get(AUTH_COOKIES.ACCESS_TOKEN_SOFT);

        removeAuthTokens();
        removeOrderGrooveToken();
        removeCartCookie();
        clearCart?.();

        if (accessToken || accessTokenSoft) {
            const response = await apiFetch<AuthResponse>(`/api/acp/auth/logout`, {
                method: 'POST',
                body: JSON.stringify({ accessToken }),
            });

            if ('success' in response && response.success) {
                console.log('User successfully logged out.');
            }
        }

        const isTreatsRewardsAuthenticatedPage = window.location.href.includes('treats-rewards/authenticated');
        const redirectUrlDefault = isTreatsRewardsAuthenticatedPage ? '/treats-rewards' : '/sign-in';
        window.location.assign(redirectUrl ?? redirectUrlDefault);
    } catch (error) {
        console.error('Error during logout:', error);
    }
}

// TODO: Review this quick later (fix to prevent multiple refresh requests)
let isRefreshingUserToken = false;

export async function refreshUserToken() {
    if (isRefreshingUserToken) {
        return;
    }

    try {
        isRefreshingUserToken = true;

        const response = await apiFetch<AuthResponse>(`/api/acp/auth/refresh-token`, {
            method: 'POST',
        });

        if ('success' in response && response.success) {
            console.log('User tokens successfully refreshed.');
        }
    } catch (error) {
        console.error('Error during refresh token:', error);
    } finally {
        isRefreshingUserToken = false;
    }
}

export const evaluateSession = async () => {
    if (isUserLoggedIn()) {
        if (checkLastActive()) {
            await logoutUser();
        } else if (isTokenExpiringSoon()) {
            await refreshUserToken();
        } else {
            setLastActiveCookie();
        }
    }
};
